import {
  DataType,
  Form,
  FormOptions,
  FormSkeleton,
  Section,
  SelectOption
} from '@globaleris/ui.library'
import { Box, Paper } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ButtonWithPermissions from '../../../../components/buttonWithPermissions/buttonWithPermissions'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { getCustomerDomain } from '../../../../redux'
import { EmptyContent } from '../../../clients/file/components/shared/emptyCard'
import { measureUnitToSelectOptions } from '../../../flexibility/flexibility.mapper'
import { fetchMeasureUnitPowerType } from '../../../flexibility/flexibility.service'
import { ContractQuantityFormModel } from '../../contracts.model'

interface ContractQuantityProps {
  contractQuantity?: ContractQuantityFormModel
  subtitle?: string
  onChangeInput?: (input: 'measureUnit', value: any) => void
  onAdd?: () => void
  children: React.ReactNode
}

export const ContractQuantityCard: React.FC<ContractQuantityProps> = ({
  contractQuantity,
  onChangeInput,
  onAdd,
  subtitle,
  children
}) => {
  const [t] = useTranslation(['contracts', 'globalerisLibrary', 'enums'])

  const [isFetchingMeasureOptions, setIsFetchingMeasureOptions] = useState<boolean>(false)
  const [measureOptions, setMeasureOptions] = useState<SelectOption[]>([])

  //Form config Starts
  const options: FormOptions = {
    columns: 3,
    elevation: 0,
    analytics: {
      customer: getCustomerDomain(),
      view: t('contracts:forms.contractQuantities.analytics')
    }
  }

  const onChange = (input: 'measureUnit', value) => {
    onChangeInput && onChangeInput(input, value)
  }

  const limitFormSections: Section[] = [
    {
      inputs: [
        {
          name: 'measureUnit',
          label: t('contracts:forms.contractQuantities.inputs.measureUnit'),
          type: DataType.SELECT,
          isLoading: isFetchingMeasureOptions,
          selectOptions: measureOptions,
          required: true,
          readOnly: !!contractQuantity?.id,
          onChange: (value, formState) => {
            onChange('measureUnit', value)
          }
        }
      ]
    }
  ]

  const fetchMeasureOptions = async () => {
    setIsFetchingMeasureOptions(true)
    await fetchMeasureUnitPowerType()
      .then(response => {
        if (response.success) {
          setMeasureOptions(measureUnitToSelectOptions(response.data ?? []))
        }
      })
      .finally(() => setIsFetchingMeasureOptions(false))
  }

  const handleAddClick = () => {
    onAdd && onAdd()
  }

  useEffect(() => {
    fetchMeasureOptions()
  }, [])

  if (!contractQuantity) {
    return <FormSkeleton columns={3} inputs={6} />
  }

  if (
    !contractQuantity.id &&
    (!contractQuantity.details || contractQuantity.details?.length === 0)
  ) {
    return (
      <CardWithTransition show={true} raised style={{ paddingTop: 20 }}>
        <EmptyContent title={t('contracts:forms.contractQuantities.emptyCard.title')}>
          <ButtonWithPermissions
            hasPermission={true}
            size="small"
            color="primary"
            variant="contained"
            onClick={handleAddClick}
          >
            {t('contracts:forms.contractQuantities.emptyCard.addButtonLabel')}
          </ButtonWithPermissions>
        </EmptyContent>
      </CardWithTransition>
    )
  }

  return (
    <>
      <Box marginTop={2}>
        <Paper elevation={3}>
          <Form
            title={t('contracts:forms.contractQuantities.titles.cardTitle')}
            subtitle={t('contracts:forms.contractQuantities.titles.cardSubtitle')}
            sections={limitFormSections}
            options={options}
            data={contractQuantity}
          />
          <Box marginX={2}>{children}</Box>
        </Paper>
      </Box>
    </>
  )
}
